import React, { useMemo, useState, useEffect } from 'react'
import FormatPersonalHealthNumber from '../../shared_components/formatPersonalHealthNumber'
import FormatPhoneNumber from '../../shared_components/formatPhoneNumber'
import GenderSelect from "../../shared_components/genderSelect"
import Select from "react-select";
import phoneIcon from '../../../images/phone_icon_cmd.svg'
import envelopeIcon from '../../../images/envelope_icon_cmd.svg'

const PatientDemographics = ({ previousPatients, draftProfile, isNewProfile }) => {
  const [selectedPatientProfile, setSelectedPatientProfile] = useState({})
  const [firstName, setFirstName] = useState(draftProfile?.first_name || "")
  const [lastName, setLastName] = useState(draftProfile?.last_name || "")
  const [dateOfBirth, setDateOfBirth] = useState(draftProfile?.dob || '')
  const [gender, setGender] = useState(draftProfile?.gender && {
    label: draftProfile?.gender,
    value: draftProfile?.gender
  } || {})
  const [personalHealthNumber, setPersonalHealthNumber] = useState(draftProfile?.personal_health_number || '')
  const [phoneNumber, setPhoneNumber] = useState(draftProfile?.preferred_phone || '')
  const [email, setEmail] = useState(draftProfile?.contact_email || '')

  const patientProfiles = useMemo(() => (
    previousPatients?.map((profile) => (
      {
        label: `${profile.name} | dob: ${profile.dob} `,
        value: profile.id,
        firstName: profile.first_name,
        lastName: profile.last_name,
        dob: profile.dob,
        personalHealthNumber: profile.personal_health_number,
        gender: profile.gender,
        phoneNumber: profile.preferred_phone,
        email: profile.contact_email
      }
    ))
  ), [previousPatients])

  const handleClearForm = () => {
    setSelectedPatientProfile({ firstName: '', lastName: '', dob: '', personalHealthNumber: '', gender: '', phoneNumber: '', email: '', id: null })
  }

  // Every setter falls back to '' rather than undefined: the effect also runs on
  // mount, when nothing is selected, and feeding undefined into a controlled
  // input flips it to uncontrolled — which drops React's view of the value and
  // makes the browser's `required` check inconsistent.
  useEffect(() => {
    setFirstName(selectedPatientProfile?.firstName || '')
    setLastName(selectedPatientProfile?.lastName || '')
    setDateOfBirth(selectedPatientProfile?.dob || '')
    setGender(selectedPatientProfile?.gender
      ? { label: selectedPatientProfile.gender, value: selectedPatientProfile.gender }
      : {})
    setPersonalHealthNumber(selectedPatientProfile?.personalHealthNumber || '')
    setPhoneNumber(selectedPatientProfile?.phoneNumber || '')
    setEmail(selectedPatientProfile?.email || '')
  }, [selectedPatientProfile])

  const attributeName = (field) => (
    isNewProfile ? `patient_profile[${field}]` : `issue[patient_profile_attributes][${field}]`
  )

  const firstNameAttributeValue = useMemo(() => attributeName('first_name'), [isNewProfile])
  const lastNameAttributeValue = useMemo(() => attributeName('last_name'), [isNewProfile])
  const dobAttributeValue = useMemo(() => attributeName('dob'), [isNewProfile])
  const genderAttributeValue = useMemo(() => attributeName('gender'), [isNewProfile])
  const personalHealthNumberAttributeValue = useMemo(() => attributeName('personal_health_number'), [isNewProfile])
  const phoneNumberAttributeValue = useMemo(() => attributeName('preferred_phone'), [isNewProfile])
  const emailAttributeValue = useMemo(() => attributeName('contact_email'), [isNewProfile])

  return (
    <div className='flex flex-col w-full gap-y-6 overflow-x-auto p-4 md:p-8 bg-slate-100'>
      {!isNewProfile &&
        <>
          <FieldGroup title='Patient Lookup'>
            <div className='flex flex-col md:flex-row md:items-center md:gap-x-6'>
              <DisplayPreviousPatients
                patientProfiles={patientProfiles}
                setSelectedPatientProfile={setSelectedPatientProfile}
                selectedPatientProfile={selectedPatientProfile}
              />
              {/* type='button' matters: this sits inside the consult form, and a
                  bare <button> would default to submit. */}
              <button
                type='button'
                onClick={handleClearForm}
                className='text-xs underline cursor-pointer whitespace-nowrap self-end md:self-auto md:mt-6'
              >
                Reset form
              </button>
            </div>
          </FieldGroup>
          <hr className='border-slate-300' />
        </>
      }
      <FieldGroup title='Identity'>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6'>
          <div className='form-group'>
            <label>First Name<span className="text-red-700">*</span></label>
            <input
              type='text'
              placeholder='Jane'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <input
              type='hidden'
              name={firstNameAttributeValue}
              value={firstName}
            />
            <input
              type='hidden'
              name={`issue[patient_profile_attributes][id]`}
              value={selectedPatientProfile?.value}
            />
          </div>
          <div className='form-group'>
            <label>Last Name<span className="text-red-700">*</span></label>
            <input
              type='text'
              placeholder='Doe'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            <input
              type='hidden'
              name={lastNameAttributeValue}
              value={lastName}
            />
          </div>
          <div className='form-group'>
            <label>Date of Birth<span className="text-red-700">*</span></label>
            <input
              type='date'
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
            />
            <input
              type='hidden'
              name={dobAttributeValue}
              value={dateOfBirth}
            />
          </div>
          <GenderSelect gender={gender} setGender={setGender} genderAttributeValue={genderAttributeValue} />
        </div>
      </FieldGroup>
      <hr className='border-slate-300' />
      <FieldGroup title='Contact and Identification'>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6'>
          <div className='form-group'>
            <label>Personal Health Number<span className="text-red-700">*</span></label>
            <FormatPersonalHealthNumber
              personalHealthNumber={personalHealthNumber}
              setPersonalHealthNumber={setPersonalHealthNumber}
              personalHealthNumberAttributeValue={personalHealthNumberAttributeValue}
            />
          </div>
          <div className='form-group'>
            <label>Phone Number<span className="text-red-700">*</span></label>
            <InputWithIcon icon={phoneIcon} alt=''>
              <FormatPhoneNumber
                fieldName={phoneNumberAttributeValue}
                value={phoneNumber}
                onValueChange={(values) => setPhoneNumber(values.formattedValue)}
                required
              />
            </InputWithIcon>
          </div>
          <div className='form-group'>
            <label>Email</label>
            <InputWithIcon icon={envelopeIcon} alt=''>
              <input
                type='email'
                name={emailAttributeValue}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </InputWithIcon>
          </div>
        </div>
      </FieldGroup>
    </div>
  )
}


const customStyles = {
  control: base => ({
    ...base,
    height: 46,
    minHeight: 46,
    border: "1px solid rgb(203 213 225)",
  })
};

export default PatientDemographics

const FieldGroup = ({ title, children }) => {
  return (
    <div className='w-full'>
      <h4 className='text-sm font-semibold uppercase tracking-wide text-slate-500'>{title}</h4>
      {children}
    </div>
  )
}

// The left padding that makes room for the icon lives in forms.css — the
// `.form-group input[type=...]` rules are too specific for a plain `pl-9`
// utility to override.
const InputWithIcon = ({ icon, alt, children }) => {
  return (
    <div className='input-with-icon relative'>
      {/* z-10: the form-group input rules set `position: relative` on a white
          background, so an unlayered icon paints underneath it. */}
      <img
        src={icon}
        alt={alt}
        className='absolute z-10 left-3 top-1/2 -translate-y-1/2 w-4 pointer-events-none'
      />
      {children}
    </div>
  )
}

const DisplayPreviousPatients = ({ patientProfiles, setSelectedPatientProfile, selectedPatientProfile }) => {
  return (
    <div className='form-group'>
      <label>Previous Patients</label>
      <Select
        options={patientProfiles || []}
        onChange={setSelectedPatientProfile}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition={'fixed'}
        value={selectedPatientProfile}
        placeholder='Search existing patients...'
      />
    </div>
  )
}
